import { useState } from 'react';
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { authApi } from 'auth/auth.api';
import { useAuth } from 'auth/AuthProvider';
import { buildInitialVerifyPayload, isNewUserCompletionRequired, remainingAttemptsFrom } from 'auth/onboarding';
import { isTokenPersistenceError } from 'auth/sessionClassification';
import { clearOtpSession, getOtpSession, saveOtpSession, setPendingOtpCode } from 'auth/otpSession';
import { ApiError } from 'core/errors/ApiError';
import { firstFieldError, humanizeApiError } from 'core/errors/mapFieldErrors';
import { useI18n } from 'core/i18n/I18nProvider';
import { destinationAfterLogin, paths } from 'core/routing/paths';
import { Button } from 'shared/components/Button';
import { OtpField } from 'shared/components/OtpField';
import { PageMeta } from 'shared/components/PageMeta';
import { useCountdown } from 'shared/hooks/useCountdown';
import { useSubmitLock } from 'shared/hooks/useSubmitLock';
import { extractDigits } from 'shared/utilities/digits';

export function OtpVerifyPage() {
  const { t, locale } = useI18n();
  const navigate = useNavigate();
  const location = useLocation();
  const { completeLogin } = useAuth();
  const { pending, tryAcquire, release } = useSubmitLock();
  const session = getOtpSession();
  const [code, setCode] = useState('');
  const [error, setError] = useState(location.state?.otpExpired ? t('auth.codeNoLongerValid') : '');
  const [fieldErrors, setFieldErrors] = useState({});
  const [remainingAttempts, setRemainingAttempts] = useState(
    session?.remaining_attempts == null ? null : Number(session.remaining_attempts),
  );
  const [retryAfter, setRetryAfter] = useState({ seconds: 0, startedAt: Date.now() });
  const retryIn = useCountdown(retryAfter.seconds, retryAfter.startedAt);
  const throttled = retryIn > 0;

  const expires = useCountdown(session?.expires_in_seconds, session?.started_at);
  const resendIn = useCountdown(session?.resend_after_seconds, session?.started_at);

  if (!session?.phone) {
    return <Navigate to={paths.auth} replace />;
  }

  async function handleVerify(event) {
    event.preventDefault();
    const normalizedCode = extractDigits(code, 6);
    if (throttled || expires === 0 || normalizedCode.length !== 6 || !tryAcquire()) {
      return;
    }
    setError('');
    setFieldErrors({});
    try {
      const data = await authApi.verifyOtp(buildInitialVerifyPayload({
        phone: session.phone,
        code: normalizedCode,
        locale,
      }));
      const user = completeLogin(data);
      const params = new URLSearchParams(location.search);
      const destination = destinationAfterLogin(user, {
        next_step: data?.next_step,
        returnTo: params.get('returnTo'),
      });
      navigate(destination, { replace: true });
      clearOtpSession();
    } catch (err) {
      if (isNewUserCompletionRequired(err)) {
        setPendingOtpCode(normalizedCode, {
          phone: session.phone,
          terms: err.data?.required_legal_documents?.terms || session.terms,
          privacy: err.data?.required_legal_documents?.privacy || session.privacy,
        });
        navigate(paths.completeProfile, { replace: true });
        return;
      }
      if (isTokenPersistenceError(err)) {
        setError(t('auth.storageUnavailable'));
      } else if (err instanceof ApiError) {
        setFieldErrors(err.errors || {});
        const remaining = remainingAttemptsFrom(err);
        if (remaining != null) {
          setRemainingAttempts(remaining);
        }
        if (err.isThrottled()) {
          if (err.retryAfterSeconds) {
            setRetryAfter({ seconds: err.retryAfterSeconds, startedAt: Date.now() });
          }
          setError(humanizeApiError(err, t));
        } else if (err.status === 503) {
          setError(t('auth.otpDeliveryUnavailable'));
        } else if (err.isUnauthorized()) {
          setError(t('auth.invalidCode'));
        } else {
          setError(humanizeApiError(err, t));
        }
      } else {
        setError(t('errors.unexpected'));
      }
      release();
    }
  }

  async function handleResend() {
    if (resendIn > 0 || throttled || !tryAcquire()) {
      return;
    }
    setError('');
    try {
      const data = await authApi.requestOtp({ phone: session.phone, channel: session.channel || 'sms' });
      saveOtpSession({
        ...data,
        started_at: Date.now(),
        pending_code: '',
        draft_full_name: session.draft_full_name || '',
        draft_profile: session.draft_profile || null,
      });
      setRemainingAttempts(data.remaining_attempts == null ? null : Number(data.remaining_attempts));
      setCode('');
      release();
    } catch (err) {
      if (err instanceof ApiError && err.isThrottled() && err.retryAfterSeconds) {
        setRetryAfter({ seconds: err.retryAfterSeconds, startedAt: Date.now() });
      }
      if (err instanceof ApiError && err.status === 503) {
        setError(t('auth.otpDeliveryUnavailable'));
      } else {
        setError(humanizeApiError(err, t));
      }
      release();
    }
  }

  return (
    <div className="auth-page">
      <PageMeta titleKey="meta.verify" />
      <form className="card auth-card" onSubmit={handleVerify}>
        <h1>{t('auth.verifyTitle')}</h1>
        <p className="muted">{t('auth.verifySubtitle', { phone: session.masked_phone || session.phone })}</p>
        <OtpField
          label={t('auth.code')}
          value={code}
          error={firstFieldError(fieldErrors, 'code')}
          onChange={setCode}
          disabled={pending}
        />
        <p className="muted" aria-live="polite">
          {expires > 0 ? t('auth.expiresIn', { seconds: expires }) : t('auth.codeExpired')}
        </p>
        {remainingAttempts != null ? (
          <p className="muted" aria-live="polite">{t('auth.remainingAttempts', { count: remainingAttempts })}</p>
        ) : null}
        {error ? <p className="field-error" role="alert">{error}</p> : null}
        <Button type="submit" block loading={pending} disabled={extractDigits(code, 6).length !== 6 || expires === 0 || throttled}>
          {throttled ? t('errors.throttledWait', { seconds: retryIn }) : t('actions.continue')}
        </Button>
        <Button type="button" tone="ghost" block disabled={resendIn > 0 || pending || throttled} onClick={handleResend}>
          {resendIn > 0 ? t('auth.resendIn', { seconds: resendIn }) : t('actions.resend')}
        </Button>
        <Link className="text-link" to={paths.auth} onClick={() => clearOtpSession()}>{t('auth.changePhone')}</Link>
      </form>
    </div>
  );
}
